import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, 
  TrendingDown,
  Activity,
  Clock,
  AlertTriangle,
  Package,
  BarChart3,
  RefreshCw,
  Loader2,
  IndianRupee,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Snail,
  Download,
  FileSpreadsheet
} from 'lucide-react';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';
import { withCsv } from '@/lib/downloadFormat';

var API = process.env.REACT_APP_BACKEND_URL + '/api';

function InventoryAnalytics() {
  var loadingState = React.useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var analyticsState = React.useState(null);
  var analytics = analyticsState[0];
  var setAnalytics = analyticsState[1];
  
  var agingState = React.useState(null);
  var aging = agingState[0];
  var setAging = agingState[1];
  
  var periodState = React.useState('30');
  var period = periodState[0];
  var setPeriod = periodState[1];

  var exportingState = React.useState('');
  var exporting = exportingState[0];
  var setExporting = exportingState[1];
  var indivFmtState = React.useState('xlsx');
  var indivFmt = indivFmtState[0];
  var setIndivFmt = indivFmtState[1];

  React.useEffect(function() {
    fetchData();
  }, [period]);

  function fetchData() {
    setLoading(true);
    var token = localStorage.getItem('token');
    
    Promise.all([
      axios.get(API + '/inventory/analytics?period=' + period, {
        headers: { Authorization: 'Bearer ' + token }
      }),
      axios.get(API + '/inventory/stock-aging', {
        headers: { Authorization: 'Bearer ' + token }
      })
    ]).then(function(responses) {
      setAnalytics(responses[0].data);
      setAging(responses[1].data);
      setLoading(false);
    }).catch(function(error) {
      toast.error('Failed to fetch analytics data');
      setLoading(false);
    });
  }

  function runIndividual(reportType) {
    return indivFmt === 'csv' ? withCsv(function() { return exportAnalytics(reportType); }) : exportAnalytics(reportType);
  }

  function exportAnalytics(reportType) {
    setExporting(reportType);
    var token = localStorage.getItem('token');
    axios.post(API + '/inventory/export/analytics', {
      report_type: reportType,
      period: period
    }, {
      headers: { Authorization: 'Bearer ' + token },
      responseType: 'blob'
    }).then(function(response) {
      var url = window.URL.createObjectURL(new Blob([response.data]));
      var link = document.createElement('a');
      link.href = url;
      var reportName = reportType.replace(/_/g, ' ').replace(/\b\w/g, function(c) { return c.toUpperCase(); });
      link.setAttribute('download', 'Inventory_Analytics_' + reportName + '_' + period + 'days_' + new Date().toISOString().slice(0,10) + '.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Analytics report exported successfully');
      setExporting('');
    }).catch(function(error) {
      toast.error('Failed to export analytics');
      setExporting('');
    });
  }

  function formatCurrency(value) {
    return '₹' + (value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  if (loading) {
    return React.createElement('div', { className: 'flex items-center justify-center h-64' },
      React.createElement(Loader2, { className: 'w-8 h-8 animate-spin text-blue-600' })
    );
  }

  var summary = analytics ? analytics.summary : {};
  var agingSummary = aging ? aging.summary : [];

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'inventory-analytics' },
    // Header with Period Selector and Export Options
    React.createElement('div', { className: 'flex items-center justify-between flex-wrap gap-4' },
      React.createElement('div', null,
        React.createElement('h2', { className: 'text-2xl font-bold text-slate-800' }, 'Inventory Analytics'),
        React.createElement('p', { className: 'text-slate-600' }, 'Performance insights for the last ' + period + ' days')
      ),
      React.createElement('div', { className: 'flex items-center gap-2 flex-wrap' },
        React.createElement('span', { className: 'text-sm text-slate-600' }, 'Period:'),
        React.createElement('select', {
          value: period,
          onChange: function(e) { setPeriod(e.target.value); },
          className: 'px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500',
          'data-testid': 'period-selector'
        },
          React.createElement('option', { value: '7' }, 'Last 7 days'),
          React.createElement('option', { value: '30' }, 'Last 30 days'),
          React.createElement('option', { value: '90' }, 'Last 90 days')
        ),
        React.createElement(Button, { 
          variant: 'outline', 
          size: 'sm', 
          onClick: fetchData,
          className: 'gap-2'
        },
          React.createElement(RefreshCw, { className: 'w-4 h-4' }),
          'Refresh'
        ),
        React.createElement(Button, { 
          variant: 'default', 
          size: 'sm', 
          onClick: function() { exportAnalytics('full_analytics'); },
          disabled: exporting === 'full_analytics',
          className: 'gap-2 bg-green-600 hover:bg-green-700',
          'data-testid': 'export-full-analytics-btn'
        },
          exporting === 'full_analytics' ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(FileSpreadsheet, { className: 'w-4 h-4' }),
          'Export All Analytics'
        ),
        React.createElement(CsvDownloadButton, { onDownload: function() { exportAnalytics('full_analytics'); }, testId: 'export-full-analytics-csv', label: 'All Analytics CSV' })
      )
    ),

    // Export Options Row
    React.createElement(Card, { className: 'bg-slate-50' },
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center gap-4 flex-wrap' },
          React.createElement('span', { className: 'text-sm font-medium text-slate-700' }, 'Export Individual Reports:'),
          React.createElement('div', { className: 'flex rounded-md border border-slate-300 overflow-hidden text-xs', 'data-testid': 'analytics-format-toggle' },
            ['xlsx', 'csv'].map(function(fmt) {
              return React.createElement('button', { key: fmt, type: 'button', onClick: function() { setIndivFmt(fmt); },
                className: 'px-2.5 py-1 transition-colors ' + (indivFmt === fmt ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'),
                'data-testid': 'analytics-format-' + fmt }, fmt === 'xlsx' ? 'Excel' : 'CSV');
            })
          ),
          React.createElement(Button, { 
            variant: 'outline', 
            size: 'sm', 
            onClick: function() { runIndividual('fast_moving'); },
            disabled: !!exporting,
            className: 'gap-1 text-amber-700 border-amber-300 hover:bg-amber-50',
            'data-testid': 'export-fast-moving-btn'
          },
            exporting === 'fast_moving' ? React.createElement(Loader2, { className: 'w-3 h-3 animate-spin' }) : React.createElement(Download, { className: 'w-3 h-3' }),
            'Fast Moving'
          ),
          React.createElement(Button, { 
            variant: 'outline', 
            size: 'sm', 
            onClick: function() { runIndividual('slow_moving'); },
            disabled: !!exporting,
            className: 'gap-1 text-slate-700 border-slate-300 hover:bg-slate-100',
            'data-testid': 'export-slow-moving-btn'
          },
            exporting === 'slow_moving' ? React.createElement(Loader2, { className: 'w-3 h-3 animate-spin' }) : React.createElement(Download, { className: 'w-3 h-3' }),
            'Slow Moving'
          ),
          React.createElement(Button, { 
            variant: 'outline', 
            size: 'sm', 
            onClick: function() { runIndividual('dead_stock'); },
            disabled: !!exporting,
            className: 'gap-1 text-red-700 border-red-300 hover:bg-red-50',
            'data-testid': 'export-dead-stock-btn'
          },
            exporting === 'dead_stock' ? React.createElement(Loader2, { className: 'w-3 h-3 animate-spin' }) : React.createElement(Download, { className: 'w-3 h-3' }),
            'Dead Stock'
          ),
          React.createElement(Button, { 
            variant: 'outline', 
            size: 'sm', 
            onClick: function() { runIndividual('stock_aging'); },
            disabled: !!exporting,
            className: 'gap-1 text-purple-700 border-purple-300 hover:bg-purple-50',
            'data-testid': 'export-stock-aging-btn'
          },
            exporting === 'stock_aging' ? React.createElement(Loader2, { className: 'w-3 h-3 animate-spin' }) : React.createElement(Download, { className: 'w-3 h-3' }),
            'Stock Aging'
          ),
          React.createElement(Button, { 
            variant: 'outline', 
            size: 'sm', 
            onClick: function() { runIndividual('high_turnover'); },
            disabled: !!exporting,
            className: 'gap-1 text-green-700 border-green-300 hover:bg-green-50',
            'data-testid': 'export-high-turnover-btn'
          },
            exporting === 'high_turnover' ? React.createElement(Loader2, { className: 'w-3 h-3 animate-spin' }) : React.createElement(Download, { className: 'w-3 h-3' }),
            'High Turnover'
          )
        )
      )
    ),

    // KPI Summary Cards
    React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4' },
      // Total Inward
      React.createElement(Card, { className: 'border-l-4 border-l-green-500' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-slate-500' }, 'Total Inward'),
              React.createElement('p', { className: 'text-2xl font-bold text-green-600' }, (summary.total_inward_qty || 0).toLocaleString()),
              React.createElement('p', { className: 'text-xs text-slate-500 mt-1' }, 'Units received')
            ),
            React.createElement(ArrowUpRight, { className: 'w-10 h-10 text-green-500 opacity-60' })
          )
        )
      ),
      
      // Total Outward
      React.createElement(Card, { className: 'border-l-4 border-l-blue-500' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-slate-500' }, 'Total Outward'),
              React.createElement('p', { className: 'text-2xl font-bold text-blue-600' }, (summary.total_outward_qty || 0).toLocaleString()),
              React.createElement('p', { className: 'text-xs text-slate-500 mt-1' }, 'Units sold/dispatched')
            ),
            React.createElement(ArrowDownRight, { className: 'w-10 h-10 text-blue-500 opacity-60' })
          )
        )
      ),
      
      // Dead Stock Value
      React.createElement(Card, { className: 'border-l-4 border-l-red-500' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-slate-500' }, 'Dead Stock Value'),
              React.createElement('p', { className: 'text-2xl font-bold text-red-600' }, formatCurrency(summary.dead_stock_value)),
              React.createElement('p', { className: 'text-xs text-slate-500 mt-1' }, (summary.dead_stock_count || 0) + ' items with no movement')
            ),
            React.createElement(AlertTriangle, { className: 'w-10 h-10 text-red-500 opacity-60' })
          )
        )
      ),
      
      // Active Products
      React.createElement(Card, { className: 'border-l-4 border-l-purple-500' },
        React.createElement(CardContent, { className: 'p-4' },
          React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement('div', null,
              React.createElement('p', { className: 'text-sm text-slate-500' }, 'Active Products'),
              React.createElement('p', { className: 'text-2xl font-bold text-purple-600' }, (summary.products_with_movement || 0).toLocaleString()),
              React.createElement('p', { className: 'text-xs text-slate-500 mt-1' }, (summary.products_without_movement || 0) + ' dormant')
            ),
            React.createElement(Activity, { className: 'w-10 h-10 text-purple-500 opacity-60' })
          )
        )
      )
    ),

    // Fast Moving & Slow Moving Section
    React.createElement('div', { className: 'grid grid-cols-1 lg:grid-cols-2 gap-6' },
      // Fast Moving Products
      React.createElement(Card, null,
        React.createElement(CardHeader, { className: 'pb-2' },
          React.createElement(CardTitle, { className: 'flex items-center gap-2 text-lg' },
            React.createElement(Zap, { className: 'w-5 h-5 text-amber-500' }),
            'Fast Moving Products'
          ),
          React.createElement('p', { className: 'text-sm text-slate-500' }, 'Top 10 by quantity sold')
        ),
        React.createElement(CardContent, null,
          React.createElement('div', { className: 'space-y-3' },
            (analytics && analytics.fast_moving || []).map(function(item, idx) {
              return React.createElement('div', { 
                key: idx, 
                className: 'flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-transparent rounded-lg border border-amber-100'
              },
                React.createElement('div', { className: 'flex items-center gap-3' },
                  React.createElement('span', { className: 'flex items-center justify-center w-8 h-8 rounded-full bg-amber-500 text-white text-sm font-bold' }, idx + 1),
                  React.createElement('div', null,
                    React.createElement('p', { className: 'font-medium text-slate-800' }, item.name),
                    React.createElement('p', { className: 'text-xs text-slate-500' }, item.brand + ' • ' + item.category)
                  )
                ),
                React.createElement('div', { className: 'text-right' },
                  React.createElement('p', { className: 'font-bold text-amber-600' }, item.outward_qty + ' ' + item.unit),
                  React.createElement('p', { className: 'text-xs text-slate-500' }, 'Turnover: ' + item.turnover_rate + 'x')
                )
              );
            })
          ),
          (!analytics || !analytics.fast_moving || analytics.fast_moving.length === 0) && 
            React.createElement('p', { className: 'text-center text-slate-500 py-4' }, 'No movement data for this period')
        )
      ),
      
      // Slow Moving Products
      React.createElement(Card, null,
        React.createElement(CardHeader, { className: 'pb-2' },
          React.createElement(CardTitle, { className: 'flex items-center gap-2 text-lg' },
            React.createElement(Snail, { className: 'w-5 h-5 text-slate-500' }),
            'Slow Moving Products'
          ),
          React.createElement('p', { className: 'text-sm text-slate-500' }, 'Low turnover items with stock')
        ),
        React.createElement(CardContent, null,
          React.createElement('div', { className: 'space-y-3' },
            (analytics && analytics.slow_moving || []).map(function(item, idx) {
              return React.createElement('div', { 
                key: idx, 
                className: 'flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200'
              },
                React.createElement('div', null,
                  React.createElement('p', { className: 'font-medium text-slate-800' }, item.name),
                  React.createElement('p', { className: 'text-xs text-slate-500' }, item.brand + ' • ' + item.category)
                ),
                React.createElement('div', { className: 'text-right' },
                  React.createElement('p', { className: 'font-medium text-slate-700' }, 'Stock: ' + item.current_stock + ' ' + item.unit),
                  React.createElement('p', { className: 'text-xs text-slate-500' }, 'Value: ' + formatCurrency(item.stock_value))
                )
              );
            })
          ),
          (!analytics || !analytics.slow_moving || analytics.slow_moving.length === 0) && 
            React.createElement('p', { className: 'text-center text-slate-500 py-4' }, 'No slow moving items detected')
        )
      )
    ),

    // Stock Aging Analysis
    React.createElement(Card, null,
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'flex items-center gap-2' },
          React.createElement(Clock, { className: 'w-5 h-5' }),
          'Stock Aging Analysis'
        ),
        React.createElement('p', { className: 'text-sm text-slate-500' }, 'Distribution of inventory by age')
      ),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-5 gap-4' },
          agingSummary.map(function(bucket, idx) {
            var colors = [
              { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
              { bg: 'bg-lime-100', text: 'text-lime-700', border: 'border-lime-300' },
              { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
              { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
              { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' }
            ];
            var color = colors[idx] || colors[4];
            
            return React.createElement('div', { 
              key: idx, 
              className: 'p-4 rounded-lg border ' + color.bg + ' ' + color.border
            },
              React.createElement('p', { className: 'text-sm font-semibold ' + color.text }, bucket.bucket),
              React.createElement('p', { className: 'text-2xl font-bold text-slate-800 mt-2' }, bucket.sku_count),
              React.createElement('p', { className: 'text-xs text-slate-600' }, 'SKUs'),
              React.createElement('div', { className: 'mt-2 pt-2 border-t border-slate-200' },
                React.createElement('p', { className: 'text-sm font-medium text-slate-700' }, formatCurrency(bucket.total_value)),
                React.createElement('p', { className: 'text-xs text-slate-500' }, bucket.total_qty + ' units')
              )
            );
          })
        )
      )
    ),

    // Dead Stock Section
    React.createElement(Card, { className: 'border-red-200' },
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'flex items-center gap-2 text-red-700' },
          React.createElement(AlertTriangle, { className: 'w-5 h-5' }),
          'Dead Stock - No Movement in ' + period + ' Days'
        ),
        React.createElement('p', { className: 'text-sm text-slate-500' }, 'Items with stock but zero transactions in the selected period')
      ),
      React.createElement(CardContent, null,
        (analytics && analytics.dead_stock && analytics.dead_stock.length > 0) ?
          React.createElement('div', { className: 'overflow-x-auto' },
            React.createElement('table', { className: 'w-full text-sm' },
              React.createElement('thead', null,
                React.createElement('tr', { className: 'border-b' },
                  React.createElement('th', { className: 'text-left p-2 font-semibold' }, 'Product'),
                  React.createElement('th', { className: 'text-left p-2 font-semibold' }, 'Brand'),
                  React.createElement('th', { className: 'text-left p-2 font-semibold' }, 'Category'),
                  React.createElement('th', { className: 'text-right p-2 font-semibold' }, 'Stock'),
                  React.createElement('th', { className: 'text-right p-2 font-semibold' }, 'Value')
                )
              ),
              React.createElement('tbody', null,
                analytics.dead_stock.slice(0, 10).map(function(item, idx) {
                  return React.createElement('tr', { key: idx, className: 'border-b hover:bg-red-50' },
                    React.createElement('td', { className: 'p-2' },
                      React.createElement('p', { className: 'font-medium' }, item.name),
                      React.createElement('p', { className: 'text-xs text-slate-500' }, item.product_id)
                    ),
                    React.createElement('td', { className: 'p-2' }, item.brand),
                    React.createElement('td', { className: 'p-2' }, item.category),
                    React.createElement('td', { className: 'p-2 text-right' }, item.current_stock + ' ' + item.unit),
                    React.createElement('td', { className: 'p-2 text-right font-medium text-red-600' }, formatCurrency(item.stock_value))
                  );
                })
              )
            )
          )
        : React.createElement('div', { className: 'text-center py-8' },
            React.createElement(Package, { className: 'w-12 h-12 text-green-500 mx-auto mb-2' }),
            React.createElement('p', { className: 'text-green-600 font-medium' }, 'No dead stock detected!'),
            React.createElement('p', { className: 'text-sm text-slate-500' }, 'All products with stock have movement')
          )
      )
    ),

    // High Turnover Products
    React.createElement(Card, null,
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'flex items-center gap-2' },
          React.createElement(TrendingUp, { className: 'w-5 h-5 text-green-500' }),
          'High Turnover Products'
        ),
        React.createElement('p', { className: 'text-sm text-slate-500' }, 'Products with the best inventory turnover rate')
      ),
      React.createElement(CardContent, null,
        React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3' },
          (analytics && analytics.high_turnover || []).slice(0, 5).map(function(item, idx) {
            return React.createElement('div', { 
              key: idx, 
              className: 'p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200'
            },
              React.createElement('p', { className: 'font-medium text-slate-800 truncate' }, item.name),
              React.createElement('p', { className: 'text-xs text-slate-500 truncate' }, item.brand),
              React.createElement('div', { className: 'mt-3 flex items-center justify-between' },
                React.createElement('span', { className: 'text-2xl font-bold text-green-600' }, item.turnover_rate + 'x'),
                React.createElement(Badge, { className: 'bg-green-500' }, 'High')
              )
            );
          })
        ),
        (!analytics || !analytics.high_turnover || analytics.high_turnover.length === 0) &&
          React.createElement('p', { className: 'text-center text-slate-500 py-4' }, 'No turnover data available')
      )
    )
  );
}

export default InventoryAnalytics;
